// "Create in ATS" — the rules of `extension-alert-client-contact-snippet.component.ng1.ts`
// (`onCreateInATS` :568 and the scope it builds for the modal, `getCreateInAtsValidEmail`,
// `validateCreateClientContactForm`, the three `getDisabled*Tooltip`s) and of the modal template's
// `ng-class` / `tooltip-enable` expressions, as pure functions over plain values.
//
// ⚠️ THE FORM IS MUTATED FIELD BY FIELD UPSTREAM, AND WHICH FIELDS EXIST IS PART OF THE REQUEST.
// `scope.clientContactForm` starts with five keys; the company fields appear only when a radio or
// a pick writes them, and they are written as `null` rather than deleted. `angular.toJson` (and
// `JSON.stringify`) drop `undefined` and keep `null`, so "never touched" and "cleared" are two
// different bodies — every helper below returns a copy with exactly the keys upstream would have
// set, and none it would not.
import {
  AddAtsClientContactContext,
  type AddAtsClientContactParams,
  type AddAtsClientContactValidationResponse,
  type AlertClientContactCompanyData,
  type EmployerAutoCompleteResult,
} from '@woo-ext/api'
import { displayDate } from '../common/displayDate.ts'

/** `ClientContactDetailsSource.ATS` — the enum's first member. */
const SOURCE_ATS = 0

/** The `IAlertClientContactDto` fields the create flow reads. */
export interface CreateInAtsContact {
  id: number
  externalId?: string | undefined
  name?: string | null | undefined
  email?: string | null | undefined
  emailSource?: number | null | undefined
  phoneNumbers?: readonly string[] | null | undefined
  lastPosition?: string | null | undefined
  city?: string | null | undefined
  region?: string | null | undefined
  country?: string | null | undefined
  currentCompanies?: readonly AlertClientContactCompanyData[] | null | undefined
  switchedCompany?: boolean | null | undefined
}

/** `IExtensionAlertCreateClientContactForm`. */
export interface CreateClientContactForm {
  name: string | null | undefined
  email: string | null | undefined
  phone: string | null | undefined
  jobTitle: string | null | undefined
  createNewEmployer: boolean
  newCompanyId?: number | null | undefined
  existingCompanyName?: string | null | undefined
  existingCompanyExternalId?: string | null | undefined
  newCompanyCity?: string | null | undefined
  newCompanyRegion?: string | null | undefined
  newCompanyCountry?: string | null | undefined
}

/** What the snippet knows about the contact's create/update requests. */
export interface CreateInAtsState {
  /** `clientContactWithCompaniesAddedByWoo`. */
  withCompaniesAddedByWoo: boolean
  /** `isUpdateClientContactInAtsSupported`. */
  updateSupported: boolean
  /** `lastCreatedFromClientContact` — epoch millis, from the tracking call or a completed create. */
  lastCreated?: number | undefined
  /** `isCreateClientContactRequestPending`. */
  createPending: boolean
  /** `isUpdateClientContactRequestPending`. */
  updatePending: boolean
}

/** `featureFlags[CreateClientContactInAts] && !!careero.createClientContactInAtsEnabled`. */
export function isCreateClientContactEnabled(
  featureFlags: Readonly<Record<string, boolean>> | undefined,
  createClientContactInAtsEnabled: boolean | undefined,
): boolean {
  return featureFlags?.CREATE_CLIENT_CONTACT_IN_ATS === true && createClientContactInAtsEnabled === true
}

/** `!!careero.updateClientContactInAtsEnabled && !featureFlags[DisableClientContactUpdateInAts]`. */
export function isUpdateClientContactInAtsSupported(
  featureFlags: Readonly<Record<string, boolean>> | undefined,
  updateClientContactInAtsEnabled: boolean | undefined,
): boolean {
  return updateClientContactInAtsEnabled === true && featureFlags?.DISABLE_CLIENT_CONTACT_UPDATE_IN_ATS !== true
}

/**
 * `computeCreateNewCompanyDisabled` — ⚠️ INVERTED AND FAIL-OPEN: only an explicit `true` hides the
 * "Create a new company" option, so a tenant that never configured the flag keeps it.
 */
export function isCreateNewCompanyDisabled(featureFlags: Readonly<Record<string, unknown>> | undefined): boolean {
  return featureFlags?.EXTENSION_CREATE_NEW_COMPANY_DISABLED === true
}

/** The current companies Woo added — the only ones "Create a new company" can push. */
export function companiesAddedByWoo(contact: Pick<CreateInAtsContact, 'currentCompanies'>): AlertClientContactCompanyData[] {
  return (contact.currentCompanies ?? []).filter((company) => company.addedByWoo)
}

/**
 * The snippet button's `ng-class` AND its `tooltip-enable` — one expression, used twice:
 * `!clientContactWithCompaniesAddedByWoo || !isUpdateClientContactInAtsSupported &&
 * (!!lastCreatedFromClientContact || isCreateClientContactRequestPending)`.
 *
 * ⚠️ WITH UPDATE SUPPORTED, A CREATED OR PENDING CONTACT STAYS CLICKABLE. The modal's "Update
 * existing contact" is the way forward then, and it is the modal's own Create button that greys out.
 */
export function isCreateInAtsButtonDisabled(state: CreateInAtsState): boolean {
  return !state.withCompaniesAddedByWoo || (!state.updateSupported && (state.lastCreated !== undefined || state.createPending))
}

/**
 * `getDisabledCreateClientContactModalTooltip` (:1235), including woo@c627765c72's switch branch.
 * `undefined` where upstream falls off the end — `uib-tooltip` then has no content and never opens.
 */
export function disabledCreateInAtsTooltip(
  state: CreateInAtsState,
  contact: Pick<CreateInAtsContact, 'switchedCompany'>,
  /** The ATS LABEL (`getAtsLabel`), not `atsName`: this only names it (5cfd3ef899). */
  atsLabel: string | undefined,
): string | undefined {
  if (!state.withCompaniesAddedByWoo) {
    // A switch can be detected without a confirmed current company to push to the ATS.
    if (contact.switchedCompany === true) {
      return `Company switch detected, current company isn't confirmed yet.`
    }
    return `The contact details in ${String(atsLabel)} are up-to-date.`
  }
  if (!state.updateSupported) {
    if (state.lastCreated !== undefined) {
      return `This contact was created on ${String(displayDate(state.lastCreated))} in ${String(atsLabel)}`
    }
    if (state.createPending) return 'Contact creation is currently in progress.'
  }
  return undefined
}

/** `getDisabledCreateClientContactTooltip` — the modal's Create button. */
export function disabledCreateClientContactTooltip(state: CreateInAtsState, atsLabel: string | undefined): string | undefined {
  if (state.lastCreated !== undefined) {
    return `This contact was created on ${String(displayDate(state.lastCreated))} in ${String(atsLabel)}`
  }
  if (state.createPending) return 'Contact creation is currently in progress.'
  return undefined
}

/** `getDisabledUpdateClientContactTooltip`. */
export const DISABLED_UPDATE_CLIENT_CONTACT_TOOLTIP = 'Contact update is currently in progress.'

function hasCompany(form: CreateClientContactForm): boolean {
  return !!form.existingCompanyExternalId || !!form.newCompanyId
}

/** The modal's two button containers — each `ng-class` and `tooltip-enable`, verbatim. */
export function createInAtsModalButtons(
  state: CreateInAtsState,
  form: CreateClientContactForm,
): { createDisabled: boolean; createTooltipEnabled: boolean; updateDisabled: boolean; updateTooltipEnabled: boolean } {
  const createdOrPending = state.lastCreated !== undefined || state.createPending
  return {
    createDisabled: state.updateSupported && (!hasCompany(form) || createdOrPending),
    createTooltipEnabled: state.updateSupported && createdOrPending,
    updateDisabled: !hasCompany(form) || state.updatePending,
    updateTooltipEnabled: state.updatePending,
  }
}

/**
 * `getCreateInAtsValidEmail` — ⚠️ AN ATS-SOURCED EMAIL IS NOT OFFERED. It is already in the ATS,
 * and the form exists to push what WOO found; a contact whose email came from the ATS (source 0)
 * or from nowhere opens the form with the field empty.
 */
export function createInAtsValidEmail(contact: Pick<CreateInAtsContact, 'email' | 'emailSource'>): string | null | undefined {
  if (!contact.emailSource || SOURCE_ATS === contact.emailSource) return null
  return contact.email
}

/**
 * `!!phoneNumbers ? phoneNumbers[0] : null` — ⚠️ AN EMPTY ARRAY GIVES `undefined`, NOT `null`, and
 * the difference survives into the request body (the field is absent rather than null).
 */
function firstPhone(contact: Pick<CreateInAtsContact, 'phoneNumbers'>): string | null | undefined {
  return contact.phoneNumbers ? contact.phoneNumbers[0] : null
}

/** `scope.clientContactForm` as `onCreateInATS`'s `init` builds it. */
export function initialCreateClientContactForm(contact: CreateInAtsContact): CreateClientContactForm {
  return {
    name: contact.name,
    email: createInAtsValidEmail(contact),
    phone: firstPhone(contact),
    jobTitle: contact.lastPosition,
    createNewEmployer: false,
  }
}

/** The `client-contact-details-updated-event` listener: email and phone re-read from the contact. */
export function formWithUpdatedContactDetails(form: CreateClientContactForm, contact: CreateInAtsContact): CreateClientContactForm {
  return { ...form, email: createInAtsValidEmail(contact), phone: firstPhone(contact) }
}

/** `updateNewCompanyForm` — a company fills the new-company fields; none clears them to `null`. */
function updateNewCompanyForm(
  form: CreateClientContactForm,
  company: AlertClientContactCompanyData | undefined,
  contact: CreateInAtsContact,
): CreateClientContactForm {
  if (company === undefined) {
    return {
      ...form,
      newCompanyId: null,
      jobTitle: contact.lastPosition,
      newCompanyCity: null,
      newCompanyRegion: null,
      newCompanyCountry: null,
    }
  }
  return {
    ...form,
    newCompanyId: company.companyId,
    jobTitle: company.jobTitle,
    newCompanyCity: company.city,
    newCompanyRegion: company.region,
    newCompanyCountry: company.country,
  }
}

/**
 * `onCompanyRadioChanged`, after `ng-model` has written `createNewEmployer`.
 *
 * ⚠️ "CREATE A NEW COMPANY" PRESELECTS THE LAST WOO COMPANY, not the first, and clears the
 * existing-employer pick and the job title before refilling the title from that company.
 */
export function formOnCompanyRadioChanged(
  form: CreateClientContactForm,
  createNewEmployer: boolean,
  contact: CreateInAtsContact,
): CreateClientContactForm {
  const next: CreateClientContactForm = { ...form, createNewEmployer }
  if (createNewEmployer) {
    const cleared: CreateClientContactForm = {
      ...next,
      existingCompanyExternalId: null,
      existingCompanyName: null,
      jobTitle: null,
    }
    const woo = companiesAddedByWoo(contact)
    const latest = woo[woo.length - 1]
    return latest === undefined ? cleared : updateNewCompanyForm(cleared, latest, contact)
  }
  return updateNewCompanyForm(next, undefined, contact)
}

/**
 * `onNewCompanySelected` — ⚠️ LOOKED UP IN ALL `currentCompanies`, not only the Woo-added ones the
 * select lists. The two agree because the select can only offer the latter.
 */
export function formOnNewCompanySelected(
  form: CreateClientContactForm,
  companyId: number | null,
  contact: CreateInAtsContact,
): CreateClientContactForm {
  const next: CreateClientContactForm = { ...form, newCompanyId: companyId }
  const company = (contact.currentCompanies ?? []).find((candidate) => candidate.companyId === companyId)
  return updateNewCompanyForm(next, company, contact)
}

/** `onExistingEmployerSelected($item)`. */
export function formOnExistingEmployerSelected(
  form: CreateClientContactForm,
  item: EmployerAutoCompleteResult,
): CreateClientContactForm {
  return { ...form, existingCompanyName: item.employerValue, existingCompanyExternalId: item.employerExternalId }
}

/**
 * The email-optional ATSes. ⚠️ HARD-CODED UPSTREAM, duplicating the server's
 * `EnvUtils.ATSType.emailRequired` — upstream's own TODO says so, and the server re-checks it in
 * `getValidationResponse`, so a wrong list here is a worse message rather than a wrong write.
 */
export function isCreateInAtsEmailRequired(atsName: string | undefined): boolean {
  return atsName !== 'Bullhorn' && atsName !== 'Bullhorn for Salesforce'
}

export const CREATE_IN_ATS_MISSING_EMAIL_MESSAGE = 'Please enter an email address'
export const CREATE_IN_ATS_GENERIC_ERROR_MESSAGE = 'The action failed. Please try again'

/** What a press of either button does before the server is asked anything. */
export type CreateInAtsPreCheck =
  /** No company chosen: the handler returns at once, and leaves any earlier error on screen. */
  | { kind: 'no-company' }
  /** A blank email on an ATS that needs one. */
  | { kind: 'error'; message: string; missingEmail: true }
  | { kind: 'ok' }

export function createInAtsPreCheck(form: CreateClientContactForm, atsName: string | undefined): CreateInAtsPreCheck {
  if (!hasCompany(form)) return { kind: 'no-company' }
  if (!form.email && isCreateInAtsEmailRequired(atsName)) {
    return { kind: 'error', message: CREATE_IN_ATS_MISSING_EMAIL_MESSAGE, missingEmail: true }
  }
  return { kind: 'ok' }
}

/**
 * The request body, field for field and in upstream's order.
 *
 * ⚠️ UPDATE SENDS NO CONTACT LOCATION. `onUpdateClientContactClicked`'s literal has no `city`,
 * `region` or `country` — the existing record keeps its own — so the two contexts are two bodies.
 */
export function buildAddAtsClientContactParams(
  form: CreateClientContactForm,
  contact: CreateInAtsContact,
  alertId: number | undefined,
  context: AddAtsClientContactContext,
): AddAtsClientContactParams {
  const head = {
    email: form.email,
    name: form.name,
    clientContactExternalId: contact.externalId,
    clientContactId: contact.id,
    alertId,
    phone: form.phone,
  }
  const location =
    context === AddAtsClientContactContext.CREATE ? { city: contact.city, region: contact.region, country: contact.country } : {}
  return {
    ...head,
    ...location,
    companyId: form.newCompanyId ? form.newCompanyId : null,
    employerExternalId: form.existingCompanyExternalId ? form.existingCompanyExternalId : null,
    jobTitle: form.jobTitle,
    newCompanyCity: form.newCompanyCity,
    newCompanyRegion: form.newCompanyRegion,
    newCompanyCountry: form.newCompanyCountry,
    context,
  }
}

/**
 * `validateCreateClientContactForm`'s mapping — `undefined` when valid.
 *
 * ⚠️ `duplicateEmail` SHOWS THE SERVER'S OWN SENTENCE ("A client contact with this email already
 * exists."), and every other key collapses to the generic message — including the API-misuse
 * errors (`company`, `clientContactExternalId`), which the client never shows verbatim.
 */
export function createInAtsValidationError(
  response: AddAtsClientContactValidationResponse,
): { message: string; missingEmail: boolean } | undefined {
  if (response.valid) return undefined
  const errors = response.validationErrors ?? {}
  if (errors.missingEmail) return { message: CREATE_IN_ATS_MISSING_EMAIL_MESSAGE, missingEmail: true }
  const duplicate = errors.duplicateEmail
  if (duplicate) return { message: duplicate, missingEmail: false }
  return { message: CREATE_IN_ATS_GENERIC_ERROR_MESSAGE, missingEmail: false }
}

/** What one press of "Create new contact" / "Update existing contact" came to. */
export type CreateInAtsSubmitOutcome =
  /** No company chosen — nothing happened, not even clearing an earlier error. */
  | { kind: 'ignored' }
  /** Refused, by the form's own email check or by the server's validation. */
  | { kind: 'error'; message: string; missingEmail: boolean }
  /** The validation call itself failed; upstream leaves that to the request-failure alert. */
  | { kind: 'validation-failed' }
  /** Valid: track, send `params`, close. */
  | { kind: 'submit'; params: AddAtsClientContactParams }

/**
 * `scope.onCreateClientContactClicked` / `scope.onUpdateClientContactClicked` — the two handlers
 * `onCreateInATS` builds, which are identical but for `context`.
 *
 * ⚠️ THE ERROR IS CLEARED BEFORE THE EMAIL CHECK AND BEFORE THE SERVER IS ASKED, so it is blank
 * while validation is in flight; `clearError` is where that happens. A press with no company
 * returns before it and leaves the previous message on screen.
 */
export async function submitCreateInAtsForm({
  form,
  contact,
  alertId,
  atsName,
  context,
  validate,
  clearError,
}: {
  form: CreateClientContactForm
  contact: CreateInAtsContact
  alertId: number | undefined
  atsName: string | undefined
  context: AddAtsClientContactContext
  validate: (params: AddAtsClientContactParams) => Promise<AddAtsClientContactValidationResponse>
  clearError?: () => void
}): Promise<CreateInAtsSubmitOutcome> {
  const check = createInAtsPreCheck(form, atsName)
  if (check.kind === 'no-company') return { kind: 'ignored' }
  clearError?.()
  if (check.kind === 'error') return { kind: 'error', message: check.message, missingEmail: true }
  const params = buildAddAtsClientContactParams(form, contact, alertId, context)
  let response: AddAtsClientContactValidationResponse
  try {
    response = await validate(params)
  } catch {
    return { kind: 'validation-failed' }
  }
  const invalid = createInAtsValidationError(response)
  if (invalid !== undefined) return { kind: 'error', ...invalid }
  return { kind: 'submit', params }
}

/** `scope.goToATSCopy` — `View in ${atsLabel || 'ATS'}` since 5cfd3ef899. */
export function createdContactGoToAtsCopy(atsLabel: string | undefined): string {
  return `View in ${atsLabel ? atsLabel : 'ATS'}`
}

/** How long the waiting modal spins before offering "Notify me" (`timePassed >= 10`). */
export const CREATE_IN_ATS_WAIT_SECONDS = 10
